import { useState } from 'react';
import { ArrowUpRight, Check, Copy, Download } from 'lucide-react';
import { speaker, photoSrcSet } from '@/data/speaker';

const bios = [
  { id: 'short', label: 'Bio curta', hint: '~50 palavras', text: speaker.bios.short },
  { id: 'medium', label: 'Bio média', hint: '~100 palavras', text: speaker.bios.medium },
  { id: 'full', label: 'Bio completa', hint: 'Para material impresso', text: speaker.bios.full },
];

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button type="button" className="press-copy" onClick={copy} data-copied={copied} aria-label={`Copiar ${label}`}>
      {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
      {copied ? 'Copiado' : 'Copiar texto'}
    </button>
  );
}

export function PressHero() {
  return (
    <section className="press-hero" aria-labelledby="press-title">
      <p className="pulse-eyebrow">Kit de imprensa</p>
      <h1 id="press-title">TUDO QUE VOCÊ<br />PRECISA PARA<br /><span>DIVULGAR.</span></h1>
      <p>
        Bios prontas para copiar, fotos em alta resolução e as informações aprovadas para material
        de divulgação, programa de evento e release. Se faltar alguma coisa, é só pedir.
      </p>
    </section>
  );
}

export function BrandFacts() {
  return (
    <section className="pulse-section" aria-labelledby="facts-title">
      <p className="pulse-eyebrow" id="facts-title">Como escrever</p>
      <dl className="press-facts">
        <div className="press-fact"><dt>Nome</dt><dd>{speaker.name}</dd></div>
        <div className="press-fact"><dt>Título</dt><dd>{speaker.jobTitle}</dd></div>
        <div className="press-fact"><dt>Descrição de uma linha</dt><dd>{speaker.roleLine}</dd></div>
      </dl>
    </section>
  );
}

export function BioBlocks() {
  return (
    <section className="pulse-section" aria-labelledby="bios-title">
      <div className="pulse-section-heading">
        <div>
          <p className="pulse-eyebrow">Biografias</p>
          <h2 id="bios-title">TRÊS TAMANHOS,<br />O MESMO TEXTO.</h2>
        </div>
        <p>Escolha pelo espaço disponível. As três versões são oficiais e podem ser publicadas como estão.</p>
      </div>
      {bios.map((bio) => (
        <article key={bio.id} className="press-bio">
          <div>
            <p className="press-bio-label">{bio.label}<span className="press-bio-count">{bio.hint}</span></p>
          </div>
          <div>
            <div className="press-bio-text">
              {bio.text.split('\n\n').map((paragraph) => <p key={paragraph.slice(0, 40)}>{paragraph}</p>)}
            </div>
            <CopyButton text={bio.text} label={bio.label} />
          </div>
        </article>
      ))}
    </section>
  );
}

export function PhotoDownloads() {
  return (
    <section className="pulse-section" aria-labelledby="photos-title">
      <div className="pulse-section-heading">
        <div>
          <p className="pulse-eyebrow">Fotos</p>
          <h2 id="photos-title">RETRATOS EM<br />ALTA RESOLUÇÃO.</h2>
        </div>
        <p>Uso livre para divulgação do evento. Crédito não é obrigatório; recorte e ajuste de cor, à vontade.</p>
      </div>
      <div className="press-photos">
        {speaker.pressKit.photos.map((photo) => (
          <div key={photo.id} className="press-photo">
            <figure>
              <img
                src={`${photo.webp}-640.webp`}
                srcSet={photoSrcSet(photo)}
                sizes="(max-width: 767px) 100vw, 45vw"
                alt={photo.alt}
                width={photo.width}
                height={photo.height}
                loading="lazy"
              />
            </figure>
            <div className="press-photo-meta">
              <span>{photo.label} · {photo.width}×{photo.height}</span>
              <a href={photo.original} download>Baixar PNG <Download size={15} aria-hidden="true" /></a>
            </div>
          </div>
        ))}
      </div>
      <div className="press-photo-meta" style={{ marginTop: '2rem' }}>
        <span>Logotipo</span>
        <a href={speaker.pressKit.logo} download>Baixar logo PNG <Download size={15} aria-hidden="true" /></a>
      </div>
    </section>
  );
}

export function PressLinks() {
  const links = [
    { label: 'Site', value: 'johnnyvaz.com.br', href: speaker.site },
    { label: 'Palestras', value: 'Temas e convite', href: speaker.links.speaking },
    { label: 'LinkedIn', value: '/in/johnnyvaz', href: speaker.social.linkedin },
    { label: 'YouTube', value: '@Johnny_Vaz', href: speaker.social.youtube },
    { label: 'Instagram', value: '@johnnyvaz1', href: speaker.social.instagram },
    { label: 'GitHub', value: '/johnnyvaz', href: speaker.social.github },
    { label: 'Imprensa', value: speaker.contact.email, href: `mailto:${speaker.contact.email}` },
  ];
  return (
    <section className="pulse-section" aria-labelledby="links-title">
      <div className="pulse-section-heading">
        <div>
          <p className="pulse-eyebrow">Links oficiais</p>
          <h2 id="links-title">ONDE CONFIRMAR<br />QUALQUER COISA.</h2>
        </div>
        <p>Para dúvidas sobre o material, fale direto por e-mail — respondo no mesmo dia útil.</p>
      </div>
      <ul className="press-links">
        {links.map((link) => (
          <li key={link.label}>
            <a href={link.href} target={link.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
              <span>{link.label}</span>
              {link.value}
              <ArrowUpRight size={16} aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
